import React from 'react';
import styled from 'styled-components';
import FileItem from '../components/FileItem';
import { motion } from 'framer-motion';

import { referralOptions } from '../constants/formSetup';
import { sendIframeHeight } from '../utils/iframePostMessage';

const UploadDesignFiles = ({ handleChange, formData, variant }) => {
  const addFile = event => {
    let newFiles = Array.from(event.target.files);
    let fileBuffer = Array.from(formData.designFiles);
    fileBuffer.push(...newFiles);

    const dT = new ClipboardEvent('').clipboardData || new DataTransfer();

    for (let file of fileBuffer) {
      dT.items.add(file);
    }
    handleChange({ designFiles: dT.files });
    sendIframeHeight();
  };

  const changeHandler = event => {
    const { name, value } = event.target;
    handleChange({ [name]: value });
  };

  const removeFile = index => {
    let fileBuffer = Array.from(formData.designFiles);
    fileBuffer.splice(index, 1);

    const dT = new ClipboardEvent('').clipboardData || new DataTransfer();

    for (let file of fileBuffer) {
      dT.items.add(file);
    }
    handleChange({ designFiles: dT.files });
    sendIframeHeight();
  };

  const extraQuestionsChange = (e, index, isMultiple) => {
    const newExtraQuestions = formData.extraQuestions.map(
      (extraQuestion, i) => {
        if (i === index) {
          let newValue =
            e.target.value === 'true'
              ? true
              : e.target.value === 'false'
              ? false
              : e.target.value;
          if (isMultiple) {
            newValue = [newValue];
            return { ...extraQuestion, values: newValue };
          }
          return { ...extraQuestion, value: newValue };
        }
        return extraQuestion;
      }
    );
    handleChange({ extraQuestions: newExtraQuestions });
  };

  return (
    <StyledFormStep
      key="font-select"
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.25 }}
    >
      <h2>Upload Your Logo(s) and/or Design(s)</h2>

      <div className="file-list">
        {formData.designFiles &&
          Array.from(formData.designFiles).map((file, index) => (
            <div key={file.name} className="file-item-container">
              <button onClick={() => removeFile(index)}>X</button>
              <FileItem key={index} file={file} />
            </div>
          ))}
      </div>

      <label htmlFor="file-upload" id="custom-file-upload">
        Click to Upload
      </label>
      <input
        id="file-upload"
        name="files"
        type="file"
        onChange={addFile}
        multiple
      />

      <p className="center">Logos in vector format (.ai, .eps, .pdf) are preferred. <a href="https://www.teamtriton.com/vector-logos.html" target="_blank" rel="noreferrer">What is a vector logo?</a></p>

      <small className="center oswald">
        <sup>*</sup>By uploading a logo/graphic, you are communicating with
        Triton that you have taken the necessary steps to obtain legal
        permission to use said logo (if necessary) and relay those
        rights/permissions to Triton for use on any and all custom
        jerseys/apparel.
      </small>

      {!!formData?.extraQuestions?.length && (
        <div className="selects-container">
          {formData.extraQuestions.map((question, index) => (
            <>
              <label className="center" htmlFor={question.id}>
                {question.question}
              </label>
              <div className="form-control custom-select">
                <select
                  name={question.id}
                  id={question.id}
                  onChange={e =>
                    extraQuestionsChange(e, index, question.type === 'multiple')
                  }
                  value={
                    question.type === 'multiple'
                      ? question.values
                      : question.value
                  }
                >
                  <option value=""></option>
                  {question.options.map(option => (
                    <option key={`${index}-${option}`} value={option}>
                      {option === true
                        ? 'Yes'
                        : option === false
                        ? 'No'
                        : option}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ))}
        </div>
      )}

      {variant !== 'boa' && (
      <div className="form-control nih-control center">
        <label className="center" htmlFor="nihDate">
          By what date do you need your actual{' '}
          {formData.sport !== 'apparel' ? 'jerseys/uniforms' : 'apparel'}{' '}
          in-hand?
        </label>
        <div>
          <small className="center oswald">
            Please note that our manufacturing turnaround time is 3-4 weeks for
            most of the year. For our full manufacturing turnaround schedule,
            please visit:{' '}
            <a
              href="https://www.teamtriton.com/turnaround-times.html"
              target="_blank"
              rel="noreferrer"
            >
              Turn Times
            </a>
          </small>
        </div>
        <input
          type="text"
          name="nihDate"
          onChange={changeHandler}
          value={formData.nihDate}
        />
        </div>
      )}

      <h2>Additional Comments</h2>

      <textarea
        name="comments"
        id="comments"
        rows="10"
        onChange={changeHandler}
        value={formData.comments}
      />

      <small className="center oswald caption">
      Any details on how you'd like your garments to look will help us deliver the design you want. If you selected a custom font, please specify which design the font applies to.
      </small>
      {formData.sport !== 'apparel' && variant !== 'boa' && (
        <a
          className="center"
          href="https://www.teamtriton.com/custom-apparel.html"
          target="_blank"
          rel="noreferrer"
        >
          Click here to view our custom apparel catalog
        </a>
      )}

      {!['boa', 'boa2', 'hrd', 'warstic'].includes(variant) && (
      <div className="selects-container">
        <label className="center" htmlFor="referral">
          How did you find us?
        </label>
        <div className="form-control custom-select">
          <select
            name="referral"
            id="referral"
            onChange={changeHandler}
            value={formData.referral}
          >
            <option value=""></option>
            {referralOptions.all.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          </div>
        </div>
      )}
    </StyledFormStep>
  );
};

const StyledFormStep = styled(motion.div)`
  padding: 50px 50px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .caption {
    margin-bottom: 15px;
  }

  .center {
    text-align: center;
    margin: 0 auto 25px;
  }

  .oswald {
    font-family: 'Oswald', sans-serif !important;
  }

  h2 {
    margin-top: 0;
    text-transform: uppercase;
    font-weight: bold;
    text-align: center;
  }

  label {
    font-size: 1.3rem;
  }

  textarea {
    width: 100%;
    max-width: 600px;
    margin-bottom: 20px;
    border-radius: 5px;
    font-size: 20px;
    border: 1px solid rgb(180, 180, 180);
    background-color: rgb(180, 180, 180);
    color: #fff;
    padding: 0.75rem;
  }

  #file-upload {
    height: 0;
    overflow: hidden;
    width: 0;
  }

  small {
    max-width: 600px;
    margin-bottom: 25px;
    font-family: sans-serif;
    font-size: 90%;

    i {
      font-family: sans-serif;
    }
  }

  #custom-file-upload {
    background: var(--red);
    border: none;
    border-radius: 5px;
    cursor: pointer;
    color: #fff !important;
    font-size: 1.5rem !important;
    padding: 10px 50px;
    text-transform: uppercase;
    outline: none;
    position: relative;
    vertical-align: middle;
    margin-bottom: 40px;
  }

  .file-list {
    display: flex;
    flex-wrap: wrap;
    max-width: 600px;
    width: 100%;
    gap: 20px;
    margin-bottom: 20px;

    .file-item-container {
      position: relative;

      button {
        position: absolute;
        top: 0;
        right: 0;
        background: #ccc;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        color: #fff;
        font-size: 1rem;
        border-top-left-radius: 0;
        border-bottom-right-radius: 0;

        &:hover {
          background: var(--red);
        }
      }
    }
  }

  .selects-container {
    width: 100%;
    max-width: 600px;
    padding: 40px 20px;
    border-top: 2px solid #cccccccc;
    border-bottom: 2px solid #cccccccc;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 40px;

    label {
      margin-bottom: -10px;
    }
  }

  .nih-control {
    max-width: 600px;
    input {
      width: 100%;
      padding: 0.75rem;
      background-color: #b4b4b4 !important;
      color: #fff;
      border: 1px solid #b4b4b4;
      border-radius: 5px;
      font-size: 20px;
      margin: 10px 0 50px;
    }
  }
`;

export default UploadDesignFiles;
